import { IReportChapterNode } from "../../mc-shared/reportSchemas/kapitelSchema";

/**
 * Regler:
 * - Toggle på parent (node med children): sätter HELA subtree (parent + alla descendants) till next.
 * - Toggle på leaf:
 *    - next=true  => bubbla upp: sätt alla ancestors till true
 *    - next=false => bara leaf blir false (ancestors påverkas inte)
 */
export const toggleChapterInTree = (nodes: IReportChapterNode[], id: string, next: boolean): IReportChapterNode[] => {
  let didChange = false;

  const disableSubtree = (n: IReportChapterNode): IReportChapterNode => ({
    ...n,
    isActive: false,
    children: n.children?.map(disableSubtree),
  });

  const setAncestorsTrue = (list: IReportChapterNode[], targetId: string): IReportChapterNode[] => {
    let changed = false;

    const out = list.map((n) => {
      if (!n.children?.length) return n;

      // om target finns i detta subtree -> sätt denna parent true + fortsätt ner
      const contains = containsId(n.children, targetId);
      if (!contains) return n;

      const newChildren = setAncestorsTrue(n.children, targetId);
      changed = true;
      return {
        ...n,
        isActive: true,
        children: newChildren,
      };
    });

    return changed ? out : list;
  };

  const containsId = (list: IReportChapterNode[], targetId: string): boolean => {
    for (const n of list) {
      if (n.id === targetId) return true;
      if (n.children?.length && containsId(n.children, targetId)) return true;
    }
    return false;
  };

  const walk = (list: IReportChapterNode[]): IReportChapterNode[] => {
    let changed = false;

    const out = list.map((n) => {
      if (n.id === id) {
        didChange = true;
        changed = true;

        // ✅ parent-toggle: slå igenom på hela subtree både för true och false
        if (n.children?.length && next === false) {
          return disableSubtree(n);
        }

        // ✅ leaf-toggle: bara leaf ändras här
        return { ...n, isActive: next };
      }

      if (!n.children?.length) return n;

      const newChildren = walk(n.children);
      if (newChildren === n.children) return n;

      changed = true;
      return { ...n, children: newChildren };
    });

    return changed ? out : list;
  };

  const toggled = walk(nodes);
  if (!didChange) return nodes;

  // ✅ leaf=true => bubbla upp ancestors=true
  if (next === true) {
    return setAncestorsTrue(toggled, id);
  }

  // ✅ leaf=false => ingen bubbla upp
  return toggled;
};

export type RuleApplyResult = {
  kapitler: IReportChapterNode[];
  messages: string[]; // unika per regel som faktiskt gjorde en auto-ändring
};

export type RuleApplyResultWithoutMessages = {
  kapitler: IReportChapterNode[];
};

export const findNodeById = (nodes: IReportChapterNode[], id: string): IReportChapterNode | null => {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const hit = findNodeById(n.children, id);
      if (hit) return hit;
    }
  }
  return null;
};

export const setNodeActiveById = (nodes: IReportChapterNode[], id: string, isActive: boolean): IReportChapterNode[] => {
  let changed = false;

  const walk = (list: IReportChapterNode[]): IReportChapterNode[] => {
    let localChanged = false;

    const out = list.map((n) => {
      if (n.id === id) {
        if (n.isActive === isActive) return n;
        localChanged = true;
        return { ...n, isActive };
      }

      if (!n.children?.length) return n;

      const newChildren = walk(n.children);
      if (newChildren === n.children) return n;

      localChanged = true;
      return { ...n, children: newChildren };
    });

    if (localChanged) changed = true;
    return localChanged ? out : list;
  };

  const res = walk(nodes);
  return changed ? res : nodes;
};

export type IFlatChapter = {
  id: string;
  title: string;
  text?: string;
  isActive: boolean;

  parentId?: string;
  /** e.g. ["myndighetskrav", "forskrifter", "dokumentasjon"] */
  path: string[];
  /** e.g. "myndighetskrav.forskrifter.dokumentasjon" */
  pathKey: string;
  /** original node (if you want full access) */
  node: IReportChapterNode;
};

export type IFlatChaptersResult = {
  /** Fast lookup: byId["dokumentasjon"].isActive */
  byId: Record<string, IFlatChapter>;
  /** Useful for iteration in templates/debugging */
  list: IFlatChapter[];
};

/**
 * Flattens a chapter tree into an object keyed by chapter id.
 * Perfect for DocxTemplater usage:
 *   chapters.byId["dokumentasjon"]?.isActive && ...
 */
export const flattenChaptersToObject = (roots: IReportChapterNode[]): IFlatChaptersResult => {
  const byId: Record<string, IFlatChapter> = {};
  const list: IFlatChapter[] = [];

  const walk = (node: IReportChapterNode, parentId: string | undefined, path: string[]) => {
    const nextPath = [...path, node.id];
    const flat: IFlatChapter = {
      id: node.id,
      title: node.title,
      text: node.text,
      isActive: Boolean(node.isActive),
      parentId,
      path: nextPath,
      pathKey: nextPath.join("."),
      node,
    };

    byId[node.id] = flat;
    list.push(flat);

    node.children?.forEach((c) => walk(c, node.id, nextPath));
  };

  roots.forEach((r) => walk(r, undefined, []));
  return { byId, list };
};
